import {
  MemoryCreationMethodSchema,
  MemoryStatusSchema,
  MemoryStrategyNameSchema,
  type MemoryRecordResponse,
} from '@terra/shared-contracts-memory';
import { z } from 'zod';

export const APPLICATION_MEMORY_OVERLAYS_QUERY_KEY =
  'application-memory-overlays';

export const MEMORY_EXTRACTION_POLL_INTERVAL_MS = 5_000;
export const MEMORY_EXTRACTION_MAX_WAIT_MS = 600_000;

export type MemorySnapshotRecord = {
  recordId: string;
  content: string;
  createdAt?: string;
};

export type ApplicationMemoryOverlay = MemoryRecordResponse & {
  submittedAt: number;
  beforeSnapshot: MemorySnapshotRecord[];
  replacesRecordId?: string;
};

const MemorySnapshotRecordSchema = z.object({
  recordId: z.string(),
  content: z.string(),
  createdAt: z.string().optional(),
});

const ApplicationMemoryOverlaySchema = z.object({
  recordId: z.string(),
  content: z.string(),
  strategy: MemoryStrategyNameSchema.optional(),
  score: z.number().optional(),
  createdAt: z.string().optional(),
  creationMethod: MemoryCreationMethodSchema.optional(),
  status: MemoryStatusSchema.optional(),
  rejectionReason: z.string().optional(),
  submittedAt: z.number(),
  beforeSnapshot: z.array(MemorySnapshotRecordSchema),
  replacesRecordId: z.string().optional(),
});

const ApplicationMemoryOverlaysSchema = z.array(ApplicationMemoryOverlaySchema);

export const getApplicationMemoryOverlaysStorageKey = (
  applicationId: string,
): string => `${APPLICATION_MEMORY_OVERLAYS_QUERY_KEY}:${applicationId}`;

export const readPersistedApplicationMemoryOverlays = (
  applicationId: string,
): ApplicationMemoryOverlay[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(
      getApplicationMemoryOverlaysStorageKey(applicationId),
    );
    if (!raw) {
      return [];
    }
    return ApplicationMemoryOverlaysSchema.parse(JSON.parse(raw));
  } catch {
    return [];
  }
};

export const writePersistedApplicationMemoryOverlays = ({
  applicationId,
  overlays,
}: {
  applicationId: string;
  overlays: ApplicationMemoryOverlay[];
}): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const key = getApplicationMemoryOverlaysStorageKey(applicationId);
  if (overlays.length === 0) {
    window.localStorage.removeItem(key);
    return;
  }

  window.localStorage.setItem(key, JSON.stringify(overlays));
};

export const isSuppressedMemoryRecordId = (recordId: string): boolean =>
  recordId.startsWith('suppressed:');

export const isSyntheticMemoryRecordId = (recordId: string): boolean =>
  recordId.startsWith('pending:') ||
  recordId.startsWith('rejected:') ||
  isSuppressedMemoryRecordId(recordId);

export const toMemorySnapshot = (
  records: MemoryRecordResponse[],
): MemorySnapshotRecord[] =>
  records
    .filter((record) => !isSyntheticMemoryRecordId(record.recordId))
    .map((record) => ({
      recordId: record.recordId,
      content: record.content,
      createdAt: record.createdAt,
    }));

export const diffMemorySnapshots = ({
  beforeRecords,
  afterRecords,
}: {
  beforeRecords: MemorySnapshotRecord[];
  afterRecords: MemorySnapshotRecord[];
}): MemorySnapshotRecord[] => {
  const beforeByRecordId = new Map(
    beforeRecords.map((record) => [record.recordId, record]),
  );

  return afterRecords.filter((afterRecord) => {
    const beforeRecord = beforeByRecordId.get(afterRecord.recordId);
    if (!beforeRecord) {
      return true;
    }
    return (
      beforeRecord.content.trim() !== afterRecord.content.trim() ||
      beforeRecord.createdAt !== afterRecord.createdAt
    );
  });
};

const toRejectedOverlay = ({
  overlay,
  rejectionReason,
}: {
  overlay: ApplicationMemoryOverlay;
  rejectionReason: string;
}): ApplicationMemoryOverlay => ({
  ...overlay,
  recordId: overlay.recordId.replace(/^pending:/, 'rejected:'),
  status: 'rejected',
  rejectionReason,
});

const MEMORY_CONTENT_TOKEN_MIN_LENGTH = 4;
const MEMORY_CONTENT_OVERLAP_RATIO = 0.5;

const normalizeMemoryContent = (content: string): string =>
  content.trim().toLowerCase().replace(/\s+/g, ' ');

const tokenizeMemoryContent = (content: string): string[] =>
  normalizeMemoryContent(content)
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= MEMORY_CONTENT_TOKEN_MIN_LENGTH);

const isRelatedMemoryContent = ({
  submittedContent,
  recordContent,
}: {
  submittedContent: string;
  recordContent: string;
}): boolean => {
  const submitted = normalizeMemoryContent(submittedContent);
  const record = normalizeMemoryContent(recordContent);
  if (!submitted || !record) {
    return false;
  }
  if (
    submitted === record ||
    submitted.includes(record) ||
    record.includes(submitted)
  ) {
    return true;
  }

  const submittedTokens = tokenizeMemoryContent(submitted);
  if (submittedTokens.length === 0) {
    return false;
  }

  const recordTokenSet = new Set(tokenizeMemoryContent(record));
  const overlapCount = submittedTokens.filter((token) =>
    recordTokenSet.has(token),
  ).length;

  return overlapCount / submittedTokens.length >= MEMORY_CONTENT_OVERLAP_RATIO;
};

const pickMatchingManualDelta = ({
  overlay,
  candidates,
}: {
  overlay: ApplicationMemoryOverlay;
  candidates: MemorySnapshotRecord[];
}): MemorySnapshotRecord | undefined =>
  candidates.find((record) =>
    isRelatedMemoryContent({
      submittedContent: overlay.content,
      recordContent: record.content,
    }),
  );

export const resolveMemoryOverlays = ({
  overlays,
  bedrockRecords,
  categoryMismatchReason,
  now = Date.now(),
}: {
  overlays: ApplicationMemoryOverlay[];
  bedrockRecords: MemoryRecordResponse[];
  categoryMismatchReason: string;
  now?: number;
}): ApplicationMemoryOverlay[] => {
  const afterSnapshot = toMemorySnapshot(bedrockRecords);
  const creationMethodByRecordId = new Map(
    bedrockRecords.map((record) => [record.recordId, record.creationMethod]),
  );
  const ordered = [...overlays].sort((a, b) => a.submittedAt - b.submittedAt);
  const resolved: ApplicationMemoryOverlay[] = [];
  const claimedDeltaRecordIds = new Set<string>();

  for (const overlay of ordered) {
    if (isSuppressedMemoryRecordId(overlay.recordId)) {
      if (
        overlay.replacesRecordId &&
        afterSnapshot.some(
          (record) => record.recordId === overlay.replacesRecordId,
        )
      ) {
        resolved.push(overlay);
      }
      continue;
    }

    if (overlay.status !== 'pending') {
      resolved.push(overlay);
      continue;
    }

    const beforeRecordIds = new Set(
      overlay.beforeSnapshot.map((record) => record.recordId),
    );

    const unclaimedNewManualDelta = diffMemorySnapshots({
      beforeRecords: overlay.beforeSnapshot,
      afterRecords: afterSnapshot,
    }).filter((record) => {
      if (claimedDeltaRecordIds.has(record.recordId)) {
        return false;
      }
      const isInPlaceEditTarget =
        overlay.replacesRecordId != null &&
        record.recordId === overlay.replacesRecordId;
      if (beforeRecordIds.has(record.recordId) && !isInPlaceEditTarget) {
        return false;
      }
      return creationMethodByRecordId.get(record.recordId) !== 'automatic';
    });

    const deltaCandidates =
      overlay.beforeSnapshot.length === 0
        ? unclaimedNewManualDelta.filter((record) => {
            if (!record.createdAt) {
              return false;
            }

            const createdAtMs = Date.parse(record.createdAt);
            return (
              !Number.isNaN(createdAtMs) &&
              createdAtMs >= overlay.submittedAt - 60_000
            );
          })
        : unclaimedNewManualDelta;

    const matchedRecord = pickMatchingManualDelta({
      overlay,
      candidates: deltaCandidates,
    });

    if (matchedRecord) {
      claimedDeltaRecordIds.add(matchedRecord.recordId);
      if (
        overlay.replacesRecordId &&
        matchedRecord.recordId !== overlay.replacesRecordId &&
        afterSnapshot.some(
          (record) => record.recordId === overlay.replacesRecordId,
        )
      ) {
        resolved.push({
          ...overlay,
          recordId: `suppressed:${overlay.replacesRecordId}`,
          status: 'active',
          beforeSnapshot: [],
        });
      }
      continue;
    }

    if (now - overlay.submittedAt >= MEMORY_EXTRACTION_MAX_WAIT_MS) {
      resolved.push(
        toRejectedOverlay({
          overlay,
          rejectionReason: categoryMismatchReason,
        }),
      );
      continue;
    }

    resolved.push(overlay);
  }

  return resolved.sort((a, b) => b.submittedAt - a.submittedAt);
};

export const mergeMemoriesWithOverlays = ({
  bedrockRecords,
  overlays,
}: {
  bedrockRecords: MemoryRecordResponse[];
  overlays: ApplicationMemoryOverlay[];
}): MemoryRecordResponse[] => {
  const bedrockIds = new Set(bedrockRecords.map((record) => record.recordId));
  const replacedRecordIds = new Set(
    overlays.flatMap((overlay) =>
      overlay.replacesRecordId &&
      (overlay.status === 'pending' ||
        isSuppressedMemoryRecordId(overlay.recordId))
        ? [overlay.replacesRecordId]
        : [],
    ),
  );
  const visibleOverlays = overlays.filter(
    (overlay) =>
      !isSuppressedMemoryRecordId(overlay.recordId) &&
      !bedrockIds.has(overlay.recordId),
  );
  const visibleBedrockRecords = bedrockRecords.filter(
    (record) => !replacedRecordIds.has(record.recordId),
  );

  return [...visibleOverlays, ...visibleBedrockRecords];
};

export const createPendingMemoryOverlay = ({
  sessionId,
  content,
  beforeSnapshot,
  submittedAt = Date.now(),
  replacesRecordId,
}: {
  sessionId: string;
  content: string;
  beforeSnapshot: MemorySnapshotRecord[];
  submittedAt?: number;
  replacesRecordId?: string;
}): ApplicationMemoryOverlay => ({
  recordId: `pending:${sessionId}`,
  content,
  creationMethod: 'manual',
  status: 'pending',
  createdAt: new Date(submittedAt).toISOString(),
  submittedAt,
  beforeSnapshot,
  replacesRecordId,
});

export const createRejectedMemoryOverlay = ({
  content,
  rejectionReason,
  submittedAt = Date.now(),
}: {
  content: string;
  rejectionReason: string;
  submittedAt?: number;
}): ApplicationMemoryOverlay => ({
  recordId: `rejected:${submittedAt}-${Math.random().toString(36).slice(2, 9)}`,
  content,
  creationMethod: 'manual',
  status: 'rejected',
  rejectionReason,
  createdAt: new Date(submittedAt).toISOString(),
  submittedAt,
  beforeSnapshot: [],
});
